import React from 'react';
import styled, { css } from 'styled-components';

export type DeviceVariant = 'desktop' | 'phone';

const Frame = styled.figure<{ $variant: DeviceVariant }>`
  position: relative;
  margin: 0;
  background: var(--bg-2);
  border: 1px solid var(--border-strong);
  box-shadow: var(--shadow-2);
  overflow: hidden;

  ${({ $variant }) =>
    $variant === 'desktop'
      ? css`
          border-radius: var(--radius-md);
          padding: 0;
        `
      : css`
          border-radius: 28px;
          padding: 6px;
        `}
`;

const Bezel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 12px;
  border-bottom: 1px solid var(--border);
  background: var(--bg-1);

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
  }
`;

const Screen = styled.div<{ $variant: DeviceVariant; $ratio: string }>`
  position: relative;
  overflow: hidden;
  background: var(--bg-1);
  aspect-ratio: ${({ $ratio }) => $ratio};
  border-radius: ${({ $variant }) => ($variant === 'phone' ? '22px' : '0')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
  }
`;

interface DeviceFrameProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  variant?: DeviceVariant;
  priority?: boolean;
  sizes?: string;
  className?: string;
}

const DeviceFrame: React.FC<DeviceFrameProps> = ({
  src,
  alt,
  width,
  height,
  variant = 'desktop',
  priority = false,
  sizes,
  className,
}) => (
  <Frame $variant={variant} className={className}>
    {variant === 'desktop' && (
      <Bezel aria-hidden="true">
        <span />
        <span />
        <span />
      </Bezel>
    )}
    <Screen $variant={variant} $ratio={`${width} / ${height}`}>
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading="eager"
        fetchPriority={priority ? 'high' : 'low'}
      />
    </Screen>
  </Frame>
);

export default DeviceFrame;
